/**
 * Auth Widget — Self-contained sign-in modal
 *
 * Configuration via window.__AUTH_WIDGET_CONFIG__
 *
 * Zero external dependencies. Vanilla JS. Works in any modern browser.
 */
(function () {
  'use strict';

  // ── Configuration ──────────────────────────────────────────────────────────
  var cfg = window.__AUTH_WIDGET_CONFIG__ || {};
  var DEFAULT_API_BASE = cfg.apiBase || 'https://business-lending-services.origenlink.com';
  var DEFAULT_APP_BASE = cfg.appBaseUrl || 'https://business-lending.mylendistry.com';
  var DEFAULT_REDIRECT_URL = cfg.redirectUrl || '/dashboard';
  var DEFAULT_WITH_CREDENTIALS = cfg.withCredentials !== false; // default true
  var DEFAULT_ENV = cfg.env || 'production';

  // ── Styles ─────────────────────────────────────────────────────────────────
  var STYLES = /* css */ `
    /* Reset — scoped to widget */
    .aw-overlay *, .aw-overlay *::before, .aw-overlay *::after {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }

    .aw-overlay, .aw-btn {
      --aw-portal-primary: var(--portal-primary, #462d78);
      --aw-portal-primary-dark: var(--portal-primary-dark, #24163e);
      --aw-portal-gradient: var(
        --portal-gradient,
        linear-gradient(135deg, var(--aw-portal-primary-dark), var(--aw-portal-primary))
      );
    }

    /* Overlay */
    .aw-overlay {
      position: fixed;
      inset: 0;
      z-index: 2147483647;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0,0,0,0.6);
      backdrop-filter: blur(4px);
      opacity: 0;
      transition: opacity 200ms ease;
      font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont,
        "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      font-size: 16px;
      line-height: 1.5;
      color: #1e293b;
      -webkit-font-smoothing: antialiased;
    }
    .aw-overlay.aw-open { opacity: 1; }

    /* Dialog card */
    .aw-dialog {
      position: relative;
      background: #fff;
      border-radius: 12px;
      box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25);
      width: 100%;
      max-width: 440px;
      max-height: 90vh;
      overflow-y: auto;
      padding: 32px;
      transform: scale(0.95) translateY(10px);
      transition: transform 200ms ease;
    }
    .aw-overlay.aw-open .aw-dialog {
      transform: scale(1) translateY(0);
    }

    /* Close button */
    .aw-close {
      position: absolute;
      top: 16px;
      right: 16px;
      width: 32px;
      height: 32px;
      border: none;
      background: transparent;
      cursor: pointer;
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #94a3b8;
      transition: color 150ms, background 150ms;
    }
    .aw-close:hover { color: #1e293b; background: #f1f5f9; }
    .aw-close svg { width: 20px; height: 20px; }

    /* Back button */
    .aw-back {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      border: none;
      background: transparent;
      cursor: pointer;
      color: #64748b;
      font-size: 14px;
      padding: 0;
      margin-bottom: 16px;
      margin-left: -4px;
      transition: color 150ms;
    }
    .aw-back:hover { color: #1e293b; }
    .aw-back svg { width: 16px; height: 16px; }

    /* Simple heading (no icon) */
    .aw-heading {
      font-size: 22px;
      font-weight: 700;
      color: #1e293b;
      margin-bottom: 6px;
    }
    .aw-subheading {
      font-size: 14px;
      color: #64748b;
      margin-bottom: 24px;
    }

    /* Form */
    .aw-form { display: flex; flex-direction: column; gap: 16px; }
    .aw-field { display: flex; flex-direction: column; gap: 6px; }
    .aw-field label {
      font-size: 14px;
      font-weight: 500;
      color: #334155;
    }
    .aw-field label .aw-required { color: #ef4444; margin-left: 2px; }

    /* Input wrapper (for icons) */
    .aw-input-wrap {
      position: relative;
      display: flex;
      align-items: center;
    }
    .aw-input-wrap .aw-input-icon {
      position: absolute;
      left: 12px;
      width: 16px;
      height: 16px;
      color: #94a3b8;
      pointer-events: none;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .aw-input-wrap .aw-input-icon svg {
      width: 16px;
      height: 16px;
    }
    .aw-input-wrap input { padding-left: 38px; }
    .aw-input-wrap.aw-input-wrap-pw input { padding-right: 38px; }
    .aw-input-wrap .aw-input-toggle {
      position: absolute;
      right: 12px;
      width: 16px;
      height: 16px;
      color: #94a3b8;
      cursor: pointer;
      background: none;
      border: none;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .aw-input-wrap .aw-input-toggle:hover { color: #475569; }
    .aw-input-wrap .aw-input-toggle svg {
      width: 16px;
      height: 16px;
    }

    /* Input */
    .aw-input {
      width: 100%;
      height: 40px;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 0 12px;
      font-size: 14px;
      color: #1e293b;
      background: #fff;
      outline: none;
      transition: border-color 150ms, box-shadow 150ms;
      font-family: inherit;
    }
    .aw-input::placeholder { color: #94a3b8; }
    .aw-input:focus {
      border-color: var(--aw-portal-primary);
      box-shadow: 0 0 0 3px rgba(70,45,120,0.12);
    }
    .aw-input.aw-input-error {
      border-color: #ef4444;
      box-shadow: 0 0 0 3px rgba(239,68,68,0.1);
    }
    .aw-input:disabled {
      background: #f8fafc;
      color: #94a3b8;
      cursor: not-allowed;
    }

    .aw-hint {
      font-size: 12px;
      color: #94a3b8;
      margin-top: -2px;
    }

    /* Buttons */
    .aw-btn {
      width: 100%;
      height: 44px;
      border: none;
      border-radius: 8px;
      font-size: 15px;
      font-weight: 600;
      cursor: pointer;
      transition: opacity 150ms, transform 50ms;
      font-family: inherit;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }
    .aw-btn svg {
      width: 20px;
      height: 20px;
      flex-shrink: 0;
    }
    .aw-btn:active { transform: scale(0.98); }
    .aw-btn:disabled { opacity: 0.5; cursor: not-allowed; }
    .aw-btn:disabled:active { transform: none; }

    .aw-btn-primary {
      background: var(--aw-portal-gradient);
      color: #fff;
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.12);
    }
    .aw-btn-primary:hover:not(:disabled) { opacity: 0.95; }

    .aw-btn-outline {
      background: #fff;
      color: #1e293b;
      border: 1px solid #e2e8f0;
    }
    .aw-btn-outline:hover:not(:disabled) { background: #f8fafc; }

    .aw-btn-ghost {
      background: transparent;
      color: #64748b;
      height: auto;
      width: auto;
      font-size: 14px;
      font-weight: 400;
      padding: 4px 0;
    }
    .aw-btn-ghost:hover { color: var(--aw-portal-primary); }

    /* Divider */
    .aw-divider {
      position: relative;
      text-align: center;
      margin: 8px 0;
    }
    .aw-divider::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 0;
      right: 0;
      height: 1px;
      background: #e2e8f0;
    }
    .aw-divider span {
      position: relative;
      background: #fff;
      padding: 0 16px;
      font-size: 13px;
      color: #94a3b8;
    }

    /* Links */
    .aw-link-row {
      text-align: center;
      font-size: 14px;
      color: #64748b;
    }
    .aw-link-row button {
      border: none;
      background: transparent;
      color: var(--aw-portal-primary);
      font-weight: 600;
      cursor: pointer;
      font-size: 14px;
      font-family: inherit;
      padding: 0;
    }
    .aw-link-row button:hover { text-decoration: underline; }
    .aw-text-center a {
      color: var(--aw-portal-primary);
      text-decoration: none;
    }
    .aw-text-center a:hover { text-decoration: underline; }

    /* Error / warning boxes */
    .aw-error-box {
      background: #fef2f2;
      border: 1px solid #fecaca;
      border-radius: 8px;
      padding: 12px 16px;
      display: flex;
      align-items: flex-start;
      gap: 10px;
      font-size: 14px;
      color: #dc2626;
    }
    .aw-error-box svg { flex-shrink: 0; width: 18px; height: 18px; margin-top: 1px; }

    .aw-field-error {
      font-size: 12px;
      color: #ef4444;
      margin-top: -2px;
    }

    /* Success */
    .aw-success-box {
      background: #f0fdf4;
      border: 1px solid #bbf7d0;
      border-radius: 8px;
      padding: 12px 16px;
      font-size: 14px;
      color: #16a34a;
      text-align: center;
    }

    /* OTP inputs */
    .aw-otp-row {
      display: flex;
      gap: 8px;
      justify-content: center;
      margin: 8px 0;
    }
    .aw-otp-input {
      width: 48px;
      height: 52px;
      text-align: center;
      font-size: 22px;
      font-weight: 600;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      outline: none;
      color: #1e293b;
      background: #fff;
      font-family: inherit;
      transition: border-color 150ms, box-shadow 150ms;
    }
    .aw-otp-input:focus {
      border-color: var(--aw-portal-primary);
      box-shadow: 0 0 0 3px rgba(70,45,120,0.12);
    }
    @media (max-width: 400px) {
      .aw-otp-input { width: 40px; height: 44px; font-size: 18px; }
      .aw-otp-row { gap: 6px; }
    }

    /* Radio buttons (channel select) */
    .aw-radio-group { display: flex; flex-direction: column; gap: 10px; }
    .aw-radio-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 14px 16px;
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      cursor: pointer;
      transition: border-color 150ms, background 150ms;
    }
    .aw-radio-item:hover { background: #f8fafc; }
    .aw-radio-item.aw-radio-selected {
      border-color: var(--aw-portal-primary);
      background: rgba(70,45,120,0.08);
    }
    .aw-radio-item input[type="radio"] {
      width: 18px; height: 18px;
      accent-color: var(--aw-portal-primary);
      cursor: pointer;
      flex-shrink: 0;
    }
    .aw-radio-item .aw-radio-label {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      color: #334155;
    }
    .aw-radio-item .aw-radio-label svg { width: 18px; height: 18px; color: #64748b; }

    /* Remember-me checkbox */
    .aw-overlay .aw-checkbox {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      -webkit-user-select: none;
      user-select: none;
    }
    .aw-overlay .aw-checkbox input[type="checkbox"] {
      -webkit-appearance: checkbox;
      appearance: auto;
      position: static;
      opacity: 1;
      display: inline-block;
      width: 16px;
      height: 16px;
      margin: 0;
      flex-shrink: 0;
      accent-color: var(--aw-portal-primary);
      cursor: pointer;
    }
    .aw-overlay .aw-checkbox span {
      font-size: 14px;
      color: #334155;
    }

    /* Loading spinner */
    .aw-spinner {
      display: inline-block;
      width: 18px; height: 18px;
      border: 2px solid rgba(255,255,255,0.3);
      border-top-color: #fff;
      border-radius: 50%;
      animation: aw-spin 600ms linear infinite;
    }
    .aw-spinner-dark {
      border-color: rgba(0,0,0,0.1);
      border-top-color: var(--aw-portal-primary);
    }
    @keyframes aw-spin { to { transform: rotate(360deg); } }

    /* Utility */
    .aw-mt-4 { margin-top: 16px; }
    .aw-mt-6 { margin-top: 24px; }
    .aw-mb-4 { margin-bottom: 16px; }
    .aw-text-center { text-align: center; }
    .aw-text-sm { font-size: 14px; }
    .aw-text-muted { color: #64748b; }
    .aw-hidden { display: none !important; }

    /* Mobile responsive */
    @media (max-width: 480px) {
      .aw-dialog { padding: 24px 20px; margin: 12px; max-height: 95vh; }
      .aw-heading { font-size: 20px; }
    }
  `;

  // ── SVG Icons ──────────────────────────────────────────────────────────────
  var ICON = {
    close:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>',
    back: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>',
    mail: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>',
    lock: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>',
    sms: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>',
    alertCircle:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>',
    eye: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>',
    eyeOff:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/><path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"/><path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"/><line x1="2" y1="2" x2="22" y2="22"/></svg>',
  };

  // ── Utility ────────────────────────────────────────────────────────────────
  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        if (k === 'className') node.className = attrs[k];
        else if (k === 'innerHTML') node.innerHTML = attrs[k];
        else if (k.indexOf('on') === 0) node.addEventListener(k.slice(2).toLowerCase(), attrs[k]);
        else node.setAttribute(k, attrs[k]);
      });
    }
    if (children) {
      (Array.isArray(children) ? children : [children]).forEach(function (c) {
        if (typeof c === 'string') node.appendChild(document.createTextNode(c));
        else if (c) node.appendChild(c);
      });
    }
    return node;
  }

  function html(str) {
    var t = document.createElement('template');
    t.innerHTML = str.trim();
    return t.content.firstChild;
  }

  function escapeHtml(s) {
    var d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
  }

  /**
   * Wires up a show/hide toggle for a password field rendered from the
   * `.aw-input-wrap-pw` markup (see ICON.eye/eyeOff). `fieldEl` is the
   * `.aw-field` node returned by `html(...)` for that field.
   */
  function attachPasswordToggle(fieldEl) {
    var wrap = fieldEl.querySelector('.aw-input-wrap-pw');
    var input = wrap.querySelector('input');
    var toggle = wrap.querySelector('.aw-input-toggle');
    toggle.addEventListener('click', function () {
      var isHidden = input.type === 'password';
      input.type = isHidden ? 'text' : 'password';
      toggle.innerHTML = isHidden ? ICON.eyeOff : ICON.eye;
      toggle.setAttribute('aria-label', isHidden ? 'Hide password' : 'Show password');
    });
  }

  // ── Remember-me Storage ───────────────────────────────────────────────────
  // Stores only the email, never the password, in the host page's
  // localStorage. Reading/writing is wrapped in try/catch because
  // localStorage can throw (Safari private mode, sandboxed iframes, blocked
  // storage) — a failure here must never break sign-in.
  var REMEMBER_ME_KEY = 'aw_remembered_email';

  function getRememberedEmail() {
    try {
      var value = window.localStorage.getItem(REMEMBER_ME_KEY);
      return typeof value === 'string' && value ? value : '';
    } catch (e) {
      return '';
    }
  }

  function setRememberedEmail(email) {
    try {
      if (email) window.localStorage.setItem(REMEMBER_ME_KEY, email);
    } catch (e) {
      /* storage may be blocked */
    }
  }

  function clearRememberedEmail() {
    try {
      window.localStorage.removeItem(REMEMBER_ME_KEY);
    } catch (e) {
      /* storage may be blocked */
    }
  }

  function truncateEmail(e) {
    if (!e) return '';
    var parts = e.split('@');
    if (parts[0].length <= 3) return e;
    return parts[0].slice(0, 3) + '***@' + parts[1];
  }

  function formatPhone(p) {
    if (!p || p.length <= 4) return '***' + p;
    return '*'.repeat(p.length - 4) + p.slice(-4);
  }

  function isValidEmail(email) {
    if (!email) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  function normalizeEmailInput(email) {
    return String(email || '').trim();
  }

  function getEmailValidationMessage(email, requireComplete) {
    var normalizedEmail = normalizeEmailInput(email);
    if (!normalizedEmail) return requireComplete ? 'Email is required.' : '';
    if (normalizedEmail.length > 120) return 'Email must be 120 characters or fewer.';
    if (!isValidEmail(normalizedEmail)) return 'Enter a valid email address.';
    return '';
  }

  function getPasswordValidationMessages(password) {
    if (!password) return ['Password is required.'];

    var requirements = [];
    if (password.length < 8 || password.length > 64) requirements.push('be 8-64 characters');
    if (!/[a-z]/.test(password)) requirements.push('include a lowercase letter');
    if (!/[A-Z]/.test(password)) requirements.push('include an uppercase letter');
    if (!/\d/.test(password)) requirements.push('include a number');
    if (!/[^A-Za-z0-9]/.test(password)) requirements.push('include a special character');

    return requirements.length ? ['Password must ' + requirements.join(', ') + '.'] : [];
  }

  function getPasswordValidationMessage(password) {
    return getPasswordValidationMessages(password).join(' ');
  }

  function getConfirmPasswordValidationMessage(password, confirmPassword, requireComplete) {
    if (!confirmPassword) return requireComplete ? 'Please confirm your password.' : '';
    if (password !== confirmPassword) return 'Passwords must match.';
    return '';
  }

  function normalizeBaseUrl(url) {
    if (!url) return '';
    return String(url).replace(/\/+$/, '');
  }

  function isAbsoluteUrl(url) {
    return /^https?:\/\//i.test(url || '');
  }

  function parseUrl(url) {
    try {
      return new URL(url);
    } catch (e) {
      return null;
    }
  }

  function inferAppBaseUrl(redirectUrl, explicitAppBaseUrl) {
    if (explicitAppBaseUrl) return normalizeBaseUrl(explicitAppBaseUrl);
    if (!isAbsoluteUrl(redirectUrl)) return '';
    var parsed = parseUrl(redirectUrl);
    return parsed ? normalizeBaseUrl(parsed.origin) : '';
  }

  function toAppDestinationPath(redirectUrl) {
    if (!redirectUrl) return '/dashboard';
    if (!isAbsoluteUrl(redirectUrl)) {
      return redirectUrl.charAt(0) === '/' ? redirectUrl : '/' + redirectUrl;
    }
    var parsed = parseUrl(redirectUrl);
    if (!parsed) return '/dashboard';
    return (parsed.pathname || '/') + (parsed.search || '') + (parsed.hash || '');
  }

  function joinUrl(base, path) {
    if (!path) return base || '';
    if (isAbsoluteUrl(path)) return path;
    if (!base) return path;
    if (path.charAt(0) !== '/') path = '/' + path;
    return normalizeBaseUrl(base) + path;
  }

  function getExternalAuthUrl(externalAuthUrls, env) {
    if (!externalAuthUrls) return '';
    return externalAuthUrls[env] || externalAuthUrls.production || '';
  }

  function getOtpPhoneLabel(phone) {
    if (!phone) return 'your phone';
    if (/^\d{1,4}$/.test(phone)) return 'ending in ' + phone;
    return formatPhone(phone);
  }

  function buildRuntimeConfig(overrides) {
    overrides = overrides || {};
    var redirectUrl = overrides.redirectUrl != null ? overrides.redirectUrl : DEFAULT_REDIRECT_URL;
    var appBaseUrl = inferAppBaseUrl(
      redirectUrl,
      overrides.appBaseUrl != null ? overrides.appBaseUrl : DEFAULT_APP_BASE
    );
    var externalAuthUrls =
      overrides.externalAuthUrls != null
        ? overrides.externalAuthUrls
        : cfg.externalAuthUrls || (cfg.auth && cfg.auth.signInUrl) || null;
    var env = overrides.env || cfg.env || DEFAULT_ENV;

    if (!appBaseUrl && !isAbsoluteUrl(redirectUrl)) {
      console.error(
        'AuthWidget requires appBaseUrl when redirectUrl is a relative app route. Set AUTHWIDGET_APP_BASE / appBaseUrl before production use.'
      );
    }

    return {
      apiBase: normalizeBaseUrl(overrides.apiBase || DEFAULT_API_BASE),
      appBaseUrl: appBaseUrl,
      redirectUrl: redirectUrl,
      redirectPath: toAppDestinationPath(redirectUrl),
      withCredentials:
        overrides.withCredentials != null ? overrides.withCredentials : DEFAULT_WITH_CREDENTIALS,
      env: env,
      externalAuthUrl: getExternalAuthUrl(externalAuthUrls, env),
      externalAuthUrls: externalAuthUrls,
      signInPhonePlaceholder:
        overrides.signInPhonePlaceholder || cfg.signInPhonePlaceholder || '+1-___-___-___',
    };
  }

  // ── API Client ─────────────────────────────────────────────────────────────
  function api(method, path, body) {
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      xhr.open(method, currentConfig.apiBase + path, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.withCredentials = currentConfig.withCredentials;
      xhr.onload = function () {
        var data;
        try {
          data = JSON.parse(xhr.responseText);
        } catch (e) {
          data = {};
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve({ status: xhr.status, data: data });
        } else {
          var err = new Error(data.message || 'Request failed');
          err.status = xhr.status;
          err.data = data;
          reject(err);
        }
      };
      xhr.onerror = function () {
        reject(new Error('Network error'));
      };
      xhr.send(body ? JSON.stringify(body) : null);
    });
  }

  // ── Widget State ───────────────────────────────────────────────────────────
  var currentConfig = buildRuntimeConfig();

  var state = {
    view: 'sign-in',
    loading: false,
    error: '',
    statusMessage: '',
    // Sign-in flow
    intentId: null,
    phone: '',
    email: '',
    password: '',
    channels: [],
    otpChannel: null,
    actionRequired: null,
    resetFlow: false,
    // null = not yet submitted this session; true/false reflects the
    // checkbox as last submitted on the sign-in form.
    rememberMe: null,
  };

  var overlay, dialog, contentEl;

  // ── Inject Styles ──────────────────────────────────────────────────────────
  function injectStyles() {
    if (document.getElementById('aw-styles')) return;
    var s = document.createElement('style');
    s.id = 'aw-styles';
    s.textContent = STYLES;
    document.head.appendChild(s);
  }

  // ── Render Engine ──────────────────────────────────────────────────────────
  function render() {
    if (!contentEl) return;
    contentEl.innerHTML = '';

    switch (state.view) {
      case 'sign-in':
        renderSignIn();
        break;
      case 'otp-channel':
        renderChannelSelect();
        break;
      case 'otp-verify':
        renderOtpVerify();
        break;
      case 'reset-password':
        renderResetPassword();
        break;
      case 'new-password':
        renderNewPassword();
        break;
      case 'success':
        renderSuccess();
        break;
    }

    // Focus first empty input, so a pre-filled remembered email doesn't
    // steal focus from the password field. Falls back to the first input
    // when nothing is pre-filled, which matches the previous behavior.
    setTimeout(function () {
      var candidates = contentEl.querySelectorAll(
        'input:not([type=hidden]):not([type=checkbox]):not([type=radio])'
      );
      var target = null;
      for (var i = 0; i < candidates.length; i++) {
        if (!candidates[i].value) {
          target = candidates[i];
          break;
        }
      }
      if (!target) target = candidates[0];
      if (target) target.focus();
    }, 50);
  }

  function goTo(view) {
    state.error = '';
    state.statusMessage = '';
    state.view = view;
    render();
  }

  function resetFlowState() {
    state.error = '';
    state.statusMessage = '';
    state.intentId = null;
    state.phone = '';
    state.email = '';
    state.password = '';
    state.channels = [];
    state.otpChannel = null;
    state.actionRequired = null;
    state.resetFlow = false;
    state.rememberMe = null;
  }

  function setCurrentConfig(overrides) {
    currentConfig = buildRuntimeConfig(overrides);
  }

  function resolvePostAuthRedirectPath(data) {
    var actionsRequired = data && data.actionsRequired;
    if (Array.isArray(actionsRequired) && actionsRequired.indexOf('CREATE_APP_INSTANCE') !== -1) {
      return '/apply';
    }
    return currentConfig.redirectPath;
  }

  function buildTokenBridgeUrl(token, refreshToken, redirectPath) {
    if (!currentConfig.appBaseUrl) return '';
    var query =
      '/token?token=' +
      encodeURIComponent(token) +
      '&refresh=' +
      encodeURIComponent(refreshToken) +
      '&app=' +
      encodeURIComponent(redirectPath || currentConfig.redirectPath);
    return joinUrl(currentConfig.appBaseUrl, query);
  }

  function getPortalTargetUrl(data) {
    var url = data && data.portalTargetUrl;
    return typeof url === 'string' ? url.trim() : '';
  }

  // The whitelist: query params carried through onto the portalTargetUrl
  // redirect. Whichever page embeds this widget may have been reached via a
  // shell redirect carrying `returnTo` (a deep link the visitor was on
  // before being sent to sign in) — this widget never validates any of
  // these, that's the shell's job once they come back, it only has to not
  // drop them on the floor. Add a name here to carry another param the
  // same way.
  var CARRY_PARAMS = ['returnTo'];

  function getIncomingCarryParams() {
    try {
      var search = new URLSearchParams(window.location.search);
      return CARRY_PARAMS.reduce(function (incoming, key) {
        var value = search.get(key);
        if (value) incoming[key] = value;
        return incoming;
      }, {});
    } catch (e) {
      return {};
    }
  }

  function appendCarryParams(url, incoming) {
    if (!url) return url;
    var pairs = CARRY_PARAMS.filter(function (key) {
      return !!incoming[key];
    }).map(function (key) {
      return key + '=' + encodeURIComponent(incoming[key]);
    });
    if (!pairs.length) return url;
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    return url + sep + pairs.join('&');
  }

  function getSuccessRedirectUrl(data) {
    // The backend owns the destination when it returns a portal-exchange URL;
    // that endpoint sets the token cookies and 302s on to the app.
    var portalTargetUrl = getPortalTargetUrl(data);
    if (portalTargetUrl) return appendCarryParams(portalTargetUrl, getIncomingCarryParams());

    var token = data && data.token;
    var refreshToken = data && data.refreshToken;
    var redirectPath = resolvePostAuthRedirectPath(data);
    if (token && refreshToken) return buildTokenBridgeUrl(token, refreshToken, redirectPath);
    return joinUrl(currentConfig.appBaseUrl, redirectPath);
  }

  function renderExternalAuth() {
    var url = currentConfig.externalAuthUrl;
    contentEl.appendChild(html('<h2 class="aw-heading">Continue to Sign In</h2>'));
    contentEl.appendChild(
      html(
        '<p class="aw-subheading">This tenant uses an external sign-in experience. Continue to the application login portal.</p>'
      )
    );
    var button = el('button', { className: 'aw-btn aw-btn-primary', type: 'button' });
    button.textContent = 'Continue';
    button.addEventListener('click', function () {
      window.location.href = url;
    });
    contentEl.appendChild(button);
  }

  // ── Views ──────────────────────────────────────────────────────────────────

  // SIGN IN
  function renderSignIn() {
    if (currentConfig.externalAuthUrl) {
      renderExternalAuth();
      return;
    }

    contentEl.appendChild(html('<h2 class="aw-heading">Sign in to Lendistry</h2>'));

    // Error
    if (state.error) {
      contentEl.appendChild(
        html(
          '<div class="aw-error-box">' +
            ICON.alertCircle +
            '<span>' +
            escapeHtml(state.error) +
            '</span></div>'
        )
      );
    }

    var form = el('form', { className: 'aw-form', novalidate: 'novalidate' });
    form.addEventListener('submit', handleSignIn);
    // Validate on submit only (no blur validation). After a failed submit the
    // field is marked touched, so the input handler re-validates as the user fixes it.
    form.addEventListener('input', handleInlineEmailFieldInput);

    // A remembered email (from a prior successful sign-in with "Remember me"
    // checked) takes effect only when the user hasn't typed anything yet in
    // this render pass — state.email carries forward what was typed, so
    // error redraws don't lose it.
    var rememberedEmail = getRememberedEmail();
    var emailValue = state.email || rememberedEmail;
    var rememberMeChecked = state.rememberMe !== null ? state.rememberMe : Boolean(rememberedEmail);

    // Email
    var emailField = html(`
      <div class="aw-field">
        <label>Email <span class="aw-required">*</span></label>
        <div class="aw-input-wrap">
          <span class="aw-input-icon">${ICON.mail}</span>
          <input class="aw-input" type="email" name="email" placeholder="Enter your email address" required autocomplete="email" />
        </div>
        <span class="aw-field-error aw-email-error" style="display:none"></span>
      </div>
    `);
    if (emailValue) emailField.querySelector('input').value = emailValue;
    form.appendChild(emailField);

    // Password — always left empty, even when the email is remembered.
    var signInPasswordField = html(`
      <div class="aw-field">
        <label>Password <span class="aw-required">*</span></label>
        <div class="aw-input-wrap aw-input-wrap-pw">
          <span class="aw-input-icon">${ICON.lock}</span>
          <input class="aw-input" type="password" name="password" placeholder="Enter your password" required autocomplete="current-password" />
          <button type="button" class="aw-input-toggle" aria-label="Show password">${ICON.eye}</button>
        </div>
      </div>
    `);
    attachPasswordToggle(signInPasswordField);
    form.appendChild(signInPasswordField);

    // Remember me
    var rememberField = html(`
      <label class="aw-checkbox">
        <input type="checkbox" name="rememberMe" />
        <span>Remember me</span>
      </label>
    `);
    rememberField.querySelector('input').checked = rememberMeChecked;
    form.appendChild(rememberField);

    // Submit
    var submitBtn = el('button', { className: 'aw-btn aw-btn-primary aw-mt-4', type: 'submit' });
    submitBtn.textContent = 'Sign In';
    form.appendChild(submitBtn);

    contentEl.appendChild(form);

    // Links (matches original AuthenticationPage layout)
    var linksWrap = el('div', {
      style: 'margin-top:24px;display:flex;flex-direction:column;gap:16px',
    });

    // "Forgot your password?"
    var forgotRow = html('<p class="aw-text-center aw-text-sm"></p>');
    var forgotBtn = el('button', {
      style:
        'border:none;background:transparent;color:#64748b;cursor:pointer;font-size:14px;font-family:inherit;padding:0',
    });
    forgotBtn.textContent = 'Forgot your password?';
    forgotBtn.addEventListener('click', function () {
      goTo('reset-password');
    });
    forgotBtn.addEventListener('mouseenter', function () {
      this.style.color = 'var(--aw-portal-primary)';
      this.style.textDecoration = 'underline';
    });
    forgotBtn.addEventListener('mouseleave', function () {
      this.style.color = '#64748b';
      this.style.textDecoration = 'none';
    });
    forgotRow.appendChild(forgotBtn);
    linksWrap.appendChild(forgotRow);

    contentEl.appendChild(linksWrap);
  }

  function handleSignIn(e) {
    e.preventDefault();
    if (state.loading) return;
    var form = e.target;
    var email = normalizeEmailInput(form.email.value);
    var password = form.password.value;

    // Remember what was typed/checked so an error redraw of this view
    // doesn't lose it (see renderSignIn).
    state.email = email;
    state.rememberMe = Boolean(form.rememberMe && form.rememberMe.checked);

    var emailError = getEmailValidationMessage(email, true);
    if (emailError) {
      form.email.value = email;
      form.email.setAttribute('data-aw-touched', 'true');
      setEmailFieldError(form.email, emailError);
      return;
    }

    if (!password) {
      state.error = 'Please fill in all fields.';
      render();
      return;
    }

    state.loading = true;
    state.error = '';
    setSubmitLoading(form, true);

    api('POST', '/account/signin/token', {
      phone: currentConfig.signInPhonePlaceholder,
      email: email,
      password: password,
    })
      .then(function (res) {
        state.intentId = res.data.intentId;
        state.phone = res.data.phone || '';
        state.email = email;
        state.password = password;
        state.channels = res.data.channelsAvailable || [];
        state.actionRequired = res.data.actionRequired || '';
        state.resetFlow = false;
        state.loading = false;

        if (state.actionRequired === 'SELECT_OTP_CHANNEL') {
          goTo('otp-channel');
        } else if (
          !state.actionRequired ||
          state.actionRequired === 'MFA_PHONE' ||
          state.actionRequired === 'MFA_EMAIL'
        ) {
          state.otpChannel =
            state.actionRequired === 'MFA_EMAIL' ? 'EMAIL' : state.channels[0] || 'PHONE_SMS';
          goTo('otp-verify');
        } else {
          state.error = 'Unsupported sign-in step: ' + state.actionRequired;
          goTo('sign-in');
        }
      })
      .catch(function (err) {
        state.loading = false;
        var errors = err.data && err.data.errors;
        if (errors && errors.length) {
          var code = errors[0].code;
          if (code === 'USER_NOT_PRESENT_OR_BAD_CREDENTIALS') {
            var pending = errors[0].info && errors[0].info.pendingAttempts;
            state.error =
              'Incorrect password.' +
              (pending != null ? ' You have ' + (pending + 1) + ' attempt(s) remaining.' : '');
          } else if (code === 'ENTITY_NOT_FOUND') {
            state.error = 'Email not found. Please check this is the email you used to register.';
          } else if (code === 'USER_IS_DISABLED') {
            state.error = 'Your account is locked. Please contact support for assistance.';
          } else {
            state.error = errors[0].message || 'Please check your email and password.';
          }
        } else {
          state.error = 'Something went wrong. Please try again.';
        }
        render();
      });
  }

  function setEmailFieldError(input, message) {
    var error = message || '';
    input.classList.toggle('aw-input-error', Boolean(error));
    var field = input.closest('.aw-field');
    if (!field) return;
    var errorEl = field.querySelector('.aw-email-error');
    if (!errorEl) return;
    errorEl.textContent = error;
    errorEl.style.display = error ? '' : 'none';
  }

  function handleInlineEmailFieldInput(e) {
    if (!e.target || e.target.name !== 'email') return;
    if (e.target.getAttribute('data-aw-touched') === 'true') {
      setEmailFieldError(e.target, getEmailValidationMessage(e.target.value, true));
    }
  }

  // OTP CHANNEL SELECT
  function renderChannelSelect() {
    var backBtn = el('button', {
      className: 'aw-back',
      type: 'button',
      innerHTML: ICON.back + '<span>Back</span>',
    });
    backBtn.addEventListener('click', function () {
      goTo('sign-in');
    });
    contentEl.appendChild(backBtn);

    contentEl.appendChild(html('<h2 class="aw-heading">Verify Your Identity</h2>'));
    contentEl.appendChild(
      html('<p class="aw-subheading">Select how you\'d like to receive your verification code:</p>')
    );

    if (state.error) {
      contentEl.appendChild(
        html(
          '<div class="aw-error-box">' +
            ICON.alertCircle +
            '<span>' +
            escapeHtml(state.error) +
            '</span></div>'
        )
      );
    }

    var form = el('form', { className: 'aw-form', novalidate: 'novalidate' });
    form.addEventListener('submit', handleChannelSelect);

    var group = el('div', { className: 'aw-radio-group' });

    state.channels.forEach(function (ch) {
      var isEmail = ch === 'EMAIL';
      var item = html(`
        <label class="aw-radio-item">
          <input type="radio" name="channel" value="${ch}" />
          <span class="aw-radio-label">
            ${isEmail ? ICON.mail : ICON.sms}
            <span>${isEmail ? 'Email: ' + escapeHtml(truncateEmail(state.email)) : 'SMS: ' + escapeHtml(formatPhone(state.phone))}</span>
          </span>
        </label>
      `);
      item.querySelector('input').addEventListener('change', function () {
        group.querySelectorAll('.aw-radio-item').forEach(function (i) {
          i.classList.remove('aw-radio-selected');
        });
        item.classList.add('aw-radio-selected');
      });
      group.appendChild(item);
    });

    form.appendChild(group);

    var submitBtn = el('button', { className: 'aw-btn aw-btn-primary aw-mt-4', type: 'submit' });
    submitBtn.textContent = 'Continue';
    form.appendChild(submitBtn);

    contentEl.appendChild(form);
  }

  function handleChannelSelect(e) {
    e.preventDefault();
    if (state.loading) return;
    var selected = e.target.querySelector('input[name="channel"]:checked');
    if (!selected) {
      state.error = 'Please select a verification method.';
      render();
      return;
    }

    state.loading = true;
    state.error = '';
    setSubmitLoading(e.target, true);

    var channel = selected.value;

    api('POST', '/account/signin/verification', {
      intent: state.intentId,
      password: state.password,
      otpChannel: channel,
    })
      .then(function (res) {
        state.loading = false;
        if (getPortalTargetUrl(res.data)) {
          handleAuthSuccess(res.data);
          return;
        }
        state.otpChannel = channel;
        goTo('otp-verify');
      })
      .catch(function (err) {
        state.loading = false;
        state.error = (err.data && err.data.message) || 'Failed to send verification code.';
        render();
      });
  }

  // OTP VERIFY
  function renderOtpVerify() {
    var backBtn = el('button', {
      className: 'aw-back',
      type: 'button',
      innerHTML: ICON.back + '<span>Back</span>',
    });
    backBtn.addEventListener('click', function () {
      if (state.resetFlow) goTo('reset-password');
      else goTo('sign-in');
    });
    contentEl.appendChild(backBtn);

    var isEmail = state.otpChannel === 'EMAIL';
    var title = isEmail ? 'Check Your Email' : 'Check Your Phone';
    var desc = isEmail
      ? 'We sent a verification code to <b>' + escapeHtml(truncateEmail(state.email)) + '</b>'
      : 'We sent a verification code to <b>' + escapeHtml(getOtpPhoneLabel(state.phone)) + '</b>';

    contentEl.appendChild(html('<h2 class="aw-heading">' + title + '</h2>'));
    contentEl.appendChild(html('<p class="aw-subheading">' + desc + '</p>'));
    contentEl.appendChild(
      html(
        '<p class="aw-text-center aw-text-sm aw-text-muted aw-mb-4">Type your 6-digit security code below</p>'
      )
    );

    if (state.error) {
      contentEl.appendChild(
        html(
          '<div class="aw-error-box aw-mb-4">' +
            ICON.alertCircle +
            '<span>' +
            escapeHtml(state.error) +
            '</span></div>'
        )
      );
    }
    if (state.statusMessage) {
      contentEl.appendChild(
        html('<div class="aw-success-box aw-mb-4">' + escapeHtml(state.statusMessage) + '</div>')
      );
    }

    var form = el('form', { className: 'aw-form', novalidate: 'novalidate' });
    form.addEventListener('submit', handleOtpVerify);

    // OTP inputs
    var otpRow = el('div', { className: 'aw-otp-row' });
    for (var i = 0; i < 6; i++) {
      var inp = el('input', {
        className: 'aw-otp-input',
        type: 'text',
        inputMode: 'numeric',
        maxLength: '1',
        pattern: '[0-9]',
        'aria-label': 'Digit ' + (i + 1),
        'data-index': String(i),
        autocomplete: i === 0 ? 'one-time-code' : 'off',
      });
      otpRow.appendChild(inp);
    }
    form.appendChild(otpRow);

    // Wire up OTP input behavior
    setTimeout(function () {
      var inputs = otpRow.querySelectorAll('.aw-otp-input');
      inputs.forEach(function (inp, idx) {
        inp.addEventListener('input', function (e) {
          var val = e.target.value.replace(/[^0-9]/g, '');
          // Handle paste of full code
          if (val.length > 1) {
            for (var j = 0; j < Math.min(val.length, 6); j++) {
              if (inputs[idx + j]) inputs[idx + j].value = val[j];
            }
            var next = Math.min(idx + val.length, 5);
            inputs[next].focus();
            return;
          }
          e.target.value = val;
          if (val && idx < 5) inputs[idx + 1].focus();
        });
        inp.addEventListener('keydown', function (e) {
          if (e.key === 'Backspace' && !e.target.value && idx > 0) {
            inputs[idx - 1].focus();
            inputs[idx - 1].value = '';
          }
        });
        inp.addEventListener('paste', function (e) {
          e.preventDefault();
          var text = e.clipboardData.getData('text').replace(/[^0-9]/g, '');
          if (text.length >= 6) {
            for (var j = 0; j < 6; j++) inputs[j].value = text[j];
            inputs[5].focus();
          }
        });
      });
    }, 60);

    // Submit
    var submitBtn = el('button', { className: 'aw-btn aw-btn-primary aw-mt-4', type: 'submit' });
    submitBtn.textContent = 'Confirm';
    form.appendChild(submitBtn);

    contentEl.appendChild(form);

    // Resend link
    var resendWrap = el('p', { className: 'aw-text-center aw-text-sm aw-text-muted aw-mt-4' });
    resendWrap.appendChild(document.createTextNode("Didn't receive the code? "));
    var resendBtn = el('button', {
      className: 'aw-btn aw-btn-ghost',
      type: 'button',
      style: 'display:inline;font-size:14px;color:var(--aw-portal-primary);font-weight:600',
    });
    resendBtn.textContent = 'Resend code';
    resendBtn.addEventListener('click', handleResendCode);
    resendWrap.appendChild(resendBtn);
    contentEl.appendChild(resendWrap);
  }

  function handleOtpVerify(e) {
    e.preventDefault();
    if (state.loading) return;
    var inputs = contentEl.querySelectorAll('.aw-otp-input');
    var code = '';
    inputs.forEach(function (inp) {
      code += inp.value;
    });

    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      state.error = 'Please enter a valid 6-digit code.';
      render();
      return;
    }

    state.loading = true;
    state.error = '';
    setSubmitLoading(e.target, true);

    var promise;

    if (state.resetFlow) {
      // Password reset OTP
      promise = api('POST', '/account/recovery/password/phone-verification', {
        code: code,
        intentId: state.intentId,
      }).then(function (res) {
        state.loading = false;
        state.resetFlow = false;
        if (getPortalTargetUrl(res.data)) {
          handleAuthSuccess(res.data);
          return;
        }
        goTo('new-password');
      });
    } else {
      // Sign-in OTP
      promise = api('POST', '/account/signin/verification', {
        intent: state.intentId,
        password: state.password,
        code: code,
        otpChannel: state.otpChannel || 'PHONE_SMS',
      }).then(function (res) {
        state.loading = false;
        handleAuthSuccess(res.data);
      });
    }

    promise.catch(function (err) {
      state.loading = false;
      var errors = err.data && err.data.errors;
      if (errors && errors.length && errors[0].code === 'VERIFICATION_CODE_ERROR') {
        state.error = 'Invalid verification code. Please try again.';
      } else {
        state.error =
          (errors && errors.length && errors[0].message) ||
          'Code verification failed. Please try again.';
      }
      render();
    });
  }

  function handleResendCode(e) {
    if (e) e.preventDefault();
    var path;
    var body;
    if (state.resetFlow) {
      path = '/account/recovery/password/resend-code';
      body = { intent: state.intentId };
    } else {
      path = '/account/signin/phone-verification/resend-code';
      body = { intent: state.intentId };
    }
    var resendBtn = contentEl.querySelector('.aw-btn-ghost');
    if (resendBtn) resendBtn.disabled = true;
    state.statusMessage = '';
    state.error = '';
    api('POST', path, body)
      .then(function () {
        state.statusMessage = 'New code has been sent!';
        render();
      })
      .catch(function (err) {
        var errors = err.data && err.data.errors;
        state.error = (errors && errors.length && errors[0].message) || 'Failed to resend code.';
        render();
      });
  }

  // RESET PASSWORD
  function renderResetPassword() {
    var backBtn = el('button', {
      className: 'aw-back',
      type: 'button',
      innerHTML: ICON.back + '<span>Back to Sign In</span>',
    });
    backBtn.addEventListener('click', function () {
      goTo('sign-in');
    });
    contentEl.appendChild(backBtn);

    contentEl.appendChild(html('<h2 class="aw-heading">Reset Password</h2>'));
    contentEl.appendChild(
      html('<p class="aw-subheading">Enter the email address associated with your account</p>')
    );

    if (state.error) {
      contentEl.appendChild(
        html(
          '<div class="aw-error-box">' +
            ICON.alertCircle +
            '<span>' +
            escapeHtml(state.error) +
            '</span></div>'
        )
      );
    }

    var form = el('form', { className: 'aw-form', novalidate: 'novalidate' });
    form.addEventListener('submit', handleResetPassword);
    // Validate on submit only (see renderSignIn).
    form.addEventListener('input', handleInlineEmailFieldInput);

    form.appendChild(
      html(`
      <div class="aw-field">
        <label>Email <span class="aw-required">*</span></label>
        <div class="aw-input-wrap">
          <span class="aw-input-icon">${ICON.mail}</span>
          <input class="aw-input" type="email" name="email" placeholder="Enter your email address" required autocomplete="email" />
        </div>
        <span class="aw-field-error aw-email-error" style="display:none"></span>
      </div>
    `)
    );

    var submitBtn = el('button', { className: 'aw-btn aw-btn-primary', type: 'submit' });
    submitBtn.textContent = 'Reset Password';
    form.appendChild(submitBtn);

    var cancelBtn = el('button', { className: 'aw-btn aw-btn-outline aw-mt-4', type: 'button' });
    cancelBtn.textContent = 'Return to Sign In';
    cancelBtn.addEventListener('click', function () {
      goTo('sign-in');
    });
    form.appendChild(cancelBtn);

    contentEl.appendChild(form);
  }

  function handleResetPassword(e) {
    e.preventDefault();
    if (state.loading) return;
    var email = normalizeEmailInput(e.target.email.value);

    var emailError = getEmailValidationMessage(email, true);
    if (emailError) {
      e.target.email.value = email;
      e.target.email.setAttribute('data-aw-touched', 'true');
      setEmailFieldError(e.target.email, emailError);
      return;
    }

    state.loading = true;
    state.error = '';
    setSubmitLoading(e.target, true);

    api('POST', '/account/recovery/password', { email: email })
      .then(function (res) {
        state.intentId = res.data.intentId;
        state.phone = res.data.phone || '';
        state.email = email;
        state.otpChannel = 'PHONE_SMS';
        state.resetFlow = true;
        // The reset flow has no "Remember me" checkbox of its own; clear any
        // value left from an earlier sign-in attempt so handleAuthSuccess
        // doesn't act on it for this flow.
        state.rememberMe = null;
        state.loading = false;
        goTo('otp-verify');
      })
      .catch(function (err) {
        state.loading = false;
        var errors = err.data && err.data.errors;
        if (errors && errors.length) {
          var code = errors[0].code;
          if (code === 'ENTITY_NOT_FOUND') state.error = 'This email is not registered.';
          else if (code === 'USER_IS_DISABLED')
            state.error = 'This account is locked. Please contact support.';
          else state.error = errors[0].message || 'Please check your email.';
        } else {
          state.error = 'Something went wrong. Please try again.';
        }
        render();
      });
  }

  // NEW PASSWORD
  function renderNewPassword() {
    contentEl.appendChild(html('<h2 class="aw-heading">Set New Password</h2>'));
    contentEl.appendChild(
      html('<p class="aw-subheading">Choose a strong password for your account</p>')
    );

    if (state.error) {
      contentEl.appendChild(
        html(
          '<div class="aw-error-box">' +
            ICON.alertCircle +
            '<span>' +
            escapeHtml(state.error) +
            '</span></div>'
        )
      );
    }

    var form = el('form', { className: 'aw-form', novalidate: 'novalidate' });
    form.addEventListener('submit', handleNewPassword);

    var newPasswordField = html(`
      <div class="aw-field">
        <label>New Password <span class="aw-required">*</span></label>
        <div class="aw-input-wrap aw-input-wrap-pw">
          <span class="aw-input-icon">${ICON.lock}</span>
          <input class="aw-input" type="password" name="password" placeholder="Enter new password" required autocomplete="new-password" minlength="8" maxlength="64" />
          <button type="button" class="aw-input-toggle" aria-label="Show password">${ICON.eye}</button>
        </div>
        <span class="aw-hint">Use 8-64 characters with uppercase, lowercase, number, and special character. Password managers and paste are supported.</span>
      </div>
    `);
    attachPasswordToggle(newPasswordField);
    form.appendChild(newPasswordField);

    var confirmNewPasswordField = html(`
      <div class="aw-field">
        <label>Confirm Password <span class="aw-required">*</span></label>
        <div class="aw-input-wrap aw-input-wrap-pw">
          <span class="aw-input-icon">${ICON.lock}</span>
          <input class="aw-input" type="password" name="confirmpassword" placeholder="Confirm new password" required autocomplete="new-password" maxlength="64" />
          <button type="button" class="aw-input-toggle" aria-label="Show password">${ICON.eye}</button>
        </div>
      </div>
    `);
    attachPasswordToggle(confirmNewPasswordField);
    form.appendChild(confirmNewPasswordField);

    var submitBtn = el('button', { className: 'aw-btn aw-btn-primary', type: 'submit' });
    submitBtn.textContent = 'Update Password';
    form.appendChild(submitBtn);

    contentEl.appendChild(form);
  }

  function handleNewPassword(e) {
    e.preventDefault();
    if (state.loading) return;
    var f = e.target;

    var passwordError = getPasswordValidationMessage(f.password.value);
    if (passwordError) {
      state.error = passwordError;
      render();
      return;
    }

    var confirmPasswordError = getConfirmPasswordValidationMessage(
      f.password.value,
      f.confirmpassword.value,
      true
    );
    if (confirmPasswordError) {
      state.error = confirmPasswordError;
      render();
      return;
    }

    state.loading = true;
    state.error = '';
    setSubmitLoading(f, true);

    api('POST', '/account/recovery/password/update', {
      password: f.password.value,
      intentId: state.intentId,
    })
      .then(function () {
        state.loading = false;
        state.error = '';
        state.passwordResetSuccess = true;
        goTo('sign-in');
      })
      .catch(function (err) {
        state.loading = false;
        state.error = (err.data && err.data.message) || 'Failed to update password.';
        render();
      });
  }

  // SUCCESS
  function renderSuccess() {
    contentEl.appendChild(
      html(`
      <div class="aw-text-center" style="padding: 24px 0;">
        <div style="width:64px;height:64px;border-radius:50%;background:#f0fdf4;display:inline-flex;align-items:center;justify-content:center;margin-bottom:16px">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </div>
        <h2 class="aw-heading">You're all set!</h2>
        <p class="aw-subheading">Redirecting you now...</p>
        <div style="margin-top:16px"><span class="aw-spinner aw-spinner-dark"></span></div>
      </div>
    `)
    );
    if (state.error) {
      contentEl.appendChild(
        html(
          '<div class="aw-error-box aw-mt-4">' +
            ICON.alertCircle +
            '<span>' +
            escapeHtml(state.error) +
            '</span></div>'
        )
      );
    }
  }

  // ── Auth Success Handler ───────────────────────────────────────────────────
  function handleAuthSuccess(data) {
    var token = data.token;
    var refreshToken = data.refreshToken;

    // Set session markers (matches sbl-application behavior)
    try {
      document.cookie = 'lendistry_tokens_access=' + Date.now() + ';path=/';
      document.cookie = 'lendistry_tokens_refresh=' + Date.now() + ';path=/';
    } catch (e) {
      /* cookie may be blocked */
    }

    // Remember (or forget) the email per the sign-in checkbox. rememberMe is
    // null when this success came through a flow with no checkbox (e.g. the
    // reset-password flow), in which case any previously remembered email is
    // left untouched.
    if (state.rememberMe === true) setRememberedEmail(state.email);
    else if (state.rememberMe === false) clearRememberedEmail();

    goTo('success');

    // Fire success callback
    if (cfg.onSuccess) {
      cfg.onSuccess({ token: token, refreshToken: refreshToken });
    }

    // Redirect
    setTimeout(function () {
      var destination = getSuccessRedirectUrl(data);
      if (!destination) {
        state.error =
          'Login succeeded, but appBaseUrl is not configured. Set AUTHWIDGET_APP_BASE before production use.';
        render();
        return;
      }
      window.location.href = destination;
    }, 1200);
  }

  // ── Loading Helper ─────────────────────────────────────────────────────────
  function setSubmitLoading(form, isLoading) {
    var btn = form.querySelector('button[type="submit"]');
    if (!btn) return;
    if (isLoading) {
      btn.disabled = true;
      btn.setAttribute('data-text', btn.textContent);
      btn.innerHTML = '<span class="aw-spinner"></span>';
    } else {
      btn.disabled = false;
      btn.textContent = btn.getAttribute('data-text') || 'Submit';
    }
  }

  // ── Open / Close ───────────────────────────────────────────────────────────
  function open(overrides) {
    if (overrides) {
      setCurrentConfig(overrides);
    }
    resetFlowState();
    state.view = 'sign-in';
    if (overlay) {
      render();
      overlay.classList.add('aw-open');
      return;
    }

    injectStyles();

    overlay = el('div', { className: 'aw-overlay', role: 'presentation' });
    dialog = el('div', {
      className: 'aw-dialog',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-label': 'Authentication',
    });

    // Close button
    var closeBtn = el('button', {
      className: 'aw-close',
      type: 'button',
      'aria-label': 'Close',
      innerHTML: ICON.close,
    });
    closeBtn.addEventListener('click', close);
    dialog.appendChild(closeBtn);

    contentEl = el('div');
    dialog.appendChild(contentEl);

    overlay.appendChild(dialog);
    document.body.appendChild(overlay);

    // ESC to close
    document.addEventListener('keydown', handleEsc);

    // Click overlay to close
    overlay.addEventListener('mousedown', function (e) {
      if (e.target === overlay) close();
    });

    // Set inert on background
    document.querySelectorAll('body > *:not(.aw-overlay)').forEach(function (el) {
      el.setAttribute('inert', '');
      el.setAttribute('aria-hidden', 'true');
    });

    render();

    // Animate in
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        overlay.classList.add('aw-open');
      });
    });
  }

  function close() {
    if (!overlay) return;
    overlay.classList.remove('aw-open');

    // Remove inert
    document.querySelectorAll('body > [inert]').forEach(function (el) {
      el.removeAttribute('inert');
      el.removeAttribute('aria-hidden');
    });

    document.removeEventListener('keydown', handleEsc);

    setTimeout(function () {
      if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
      overlay = null;
      dialog = null;
      contentEl = null;
    }, 200);
  }

  function handleEsc(e) {
    if (e.key === 'Escape') close();
  }

  // ── Mount Points ───────────────────────────────────────────────────────────
  function initTriggers() {
    var containers = document.querySelectorAll('.auth-widget-container');
    containers.forEach(function (container) {
      // Don't double-init
      if (container.getAttribute('data-aw-init')) return;
      container.setAttribute('data-aw-init', 'true');

      var overrides = {
        redirectUrl: container.getAttribute('data-redirect-url') || undefined,
        appBaseUrl: container.getAttribute('data-app-base-url') || undefined,
        env: container.getAttribute('data-env') || undefined,
      };

      var btn = el('button', {
        className: 'aw-btn aw-btn-primary',
        type: 'button',
        style: 'width:auto;padding:0 32px;',
      });

      btn.textContent = 'Sign In';

      btn.addEventListener('click', function () {
        open(overrides);
      });

      container.appendChild(btn);
    });
  }

  // ── Public API ─────────────────────────────────────────────────────────────
  window.AuthWidget = {
    open: function (options) {
      if (typeof options === 'string') {
        console.warn(
          'AuthWidget.open("' +
            options +
            '") is deprecated: sign-up has been removed and the widget is sign-in only. Call AuthWidget.open() with no argument (or an options object).'
        );
        open({});
        return;
      }
      open(options && typeof options === 'object' ? options : {});
    },
    close: close,
    init: initTriggers,
  };

  // ── Auto-init on DOM ready ─────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTriggers);
  } else {
    initTriggers();
  }
})();
